import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, PLATFORM_ID, booleanAttribute, Input, Directive, NgModule } from '@angular/core';
import { createElement, getFirstFocusableElement, focus, getLastFocusableElement } from '@primeuix/utils';
import { BaseComponent } from 'primeng/basecomponent';

/**
 * Focus Trap keeps focus within a certain DOM element while tabbing.
 * @group Components
 */
let FocusTrap = /*#__PURE__*/(() => {
  class FocusTrap extends BaseComponent {
    /**
     * When set as true, focus wouldn't be managed.
     * @group Props
     */
    pFocusTrapDisabled = false;
    platformId = inject(PLATFORM_ID);
    document = inject(DOCUMENT);
    firstHiddenFocusableElement;
    lastHiddenFocusableElement;
    onInit() {
      if (isPlatformBrowser(this.platformId) && !this.pFocusTrapDisabled) {
        !this.firstHiddenFocusableElement && !this.lastHiddenFocusableElement && this.createHiddenFocusableElements();
      }
    }
    onChanges(changes) {
      if (changes.pFocusTrapDisabled && isPlatformBrowser(this.platformId)) {
        if (changes.pFocusTrapDisabled.currentValue) {
          this.removeHiddenFocusableElements();
        } else {
          this.createHiddenFocusableElements();
        }
      }
    }
    removeHiddenFocusableElements() {
      if (this.firstHiddenFocusableElement && this.firstHiddenFocusableElement.parentNode) {
        this.firstHiddenFocusableElement.parentNode.removeChild(this.firstHiddenFocusableElement);
      }
      if (this.lastHiddenFocusableElement && this.lastHiddenFocusableElement.parentNode) {
        this.lastHiddenFocusableElement.parentNode.removeChild(this.lastHiddenFocusableElement);
      }
    }
    getComputedSelector(selector) {
      return `:not(.p-hidden-focusable):not([data-p-hidden-focusable="true"])${selector ?? ''}`;
    }
    createHiddenFocusableElements() {
      const tabindex = '0';
      const createFocusableElement = onFocus => {
        return createElement('span', {
          class: 'p-hidden-accessible p-hidden-focusable',
          tabindex,
          role: 'presentation',
          'aria-hidden': true,
          'data-p-hidden-accessible': true,
          'data-p-hidden-focusable': true,
          onFocus: onFocus?.bind(this)
        });
      };
      this.firstHiddenFocusableElement = createFocusableElement(this.onFirstHiddenElementFocus);
      this.lastHiddenFocusableElement = createFocusableElement(this.onLastHiddenElementFocus);
      this.firstHiddenFocusableElement.setAttribute('data-pc-section', 'firstfocusableelement');
      this.lastHiddenFocusableElement.setAttribute('data-pc-section', 'lastfocusableelement');
      this.el.nativeElement.prepend(this.firstHiddenFocusableElement);
      this.el.nativeElement.append(this.lastHiddenFocusableElement);
    }
    onFirstHiddenElementFocus(event) {
      const {
        currentTarget,
        relatedTarget
      } = event;
      const focusableElement = relatedTarget === this.lastHiddenFocusableElement || !this.el.nativeElement?.contains(relatedTarget) ? getFirstFocusableElement(currentTarget.parentElement, ':not(.p-hidden-focusable)') : this.lastHiddenFocusableElement;
      focus(focusableElement);
    }
    onLastHiddenElementFocus(event) {
      const {
        currentTarget,
        relatedTarget
      } = event;
      const focusableElement = relatedTarget === this.firstHiddenFocusableElement || !this.el.nativeElement?.contains(relatedTarget) ? getLastFocusableElement(currentTarget.parentElement, ':not(.p-hidden-focusable)') : this.firstHiddenFocusableElement;
      focus(focusableElement);
    }
    static ɵfac = /* @__PURE__ */(() => {
      let ɵFocusTrap_BaseFactory;
      return function FocusTrap_Factory(__ngFactoryType__) {
        return (ɵFocusTrap_BaseFactory || (ɵFocusTrap_BaseFactory = i0.ɵɵgetInheritedFactory(FocusTrap)))(__ngFactoryType__ || FocusTrap);
      };
    })();
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: FocusTrap,
      selectors: [["", "pFocusTrap", ""]],
      inputs: {
        pFocusTrapDisabled: [2, "pFocusTrapDisabled", "pFocusTrapDisabled", booleanAttribute]
      },
      features: [i0.ɵɵInheritDefinitionFeature]
    });
  }
  return FocusTrap;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FocusTrapModule = /*#__PURE__*/(() => {
  class FocusTrapModule {
    static ɵfac = function FocusTrapModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FocusTrapModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: FocusTrapModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return FocusTrapModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { FocusTrap, FocusTrapModule };
